import type { FilterItem } from "@/routes/MessagesSMSCompose/RecipientsFilter";

export type FiltersOperator = "and" | "or";
export const DEFAULT_FILTERS_OPERATOR: FiltersOperator = "and";

const TEXT_FIELD_FILTER_TYPES = new Set([
  "firstName",
  "lastName",
  "email",
  "phoneNumber",
  "instagramUsername",
]);

const TEXT_FIELD_OPERATORS = new Set([
  "contains",
  "equals",
  "starts_with",
  "ends_with",
]);

const FIELD_EXISTS_OPERATORS = new Set(["exists", "does_not_exist"]);

const LAST_ACTIVE_COMPARISON_OPERATORS = new Set(["gte", "lte", "eq", "gt", "lt"]);

const YMD_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function filterItemToApiFilter(
  item: FilterItem
): Record<string, unknown> | null {
  if (item.filterType === "countryCode") {
    if (!item.countryCodes?.length) return null;
    return { filterType: "countryCode", countryCodes: item.countryCodes };
  }
  if (item.filterType === "page") {
    if (
      !item.operator ||
      !item.pageId ||
      !["has_submitted", "has_not_submitted"].includes(item.operator)
    )
      return null;
    const out: Record<string, unknown> = {
      filterType: "page",
      operator: item.operator,
      pageId: item.pageId,
    };
    const fid = item.formHtmlId?.trim();
    if (fid) {
      out.formHtmlId = fid;
    }
    return out;
  }
  if (item.filterType === "city" && item.operator === "is_within") {
    if (
      item.locationLat == null ||
      item.locationLng == null ||
      !item.filterValue
    )
      return null;
    return {
      filterType: "city",
      operator: "is_within",
      location: { lat: item.locationLat, lng: item.locationLng },
      value: parseFloat(item.filterValue),
    };
  }
  if (item.filterType === "contactGroup") {
    if (!item.groupId) return null;
    return { filterType: "contactGroup", groupId: item.groupId };
  }
  if (TEXT_FIELD_FILTER_TYPES.has(item.filterType)) {
    if (!item.operator) return null;
    if (FIELD_EXISTS_OPERATORS.has(item.operator)) {
      return {
        filterType: item.filterType,
        operator: item.operator,
      };
    }
    if (!TEXT_FIELD_OPERATORS.has(item.operator)) return null;
    const v = String(item.filterValue ?? "").trim();
    if (!v) return null;
    return {
      filterType: item.filterType,
      operator: item.operator,
      value: v,
    };
  }
  if (item.filterType === "product") {
    if (
      (item.operator !== "has_purchased" &&
        item.operator !== "has_not_purchased" &&
        item.operator !== "has_pre_ordered") ||
      !item.productId
    )
      return null;
    const out: Record<string, unknown> = {
      filterType: "product",
      operator: item.operator,
      productId: item.productId,
    };
    if (item.variantId?.trim()) {
      out.variantId = item.variantId.trim();
    }
    return out;
  }
  if (item.filterType === "numPurchases") {
    const allowed = new Set(["gte", "lte", "eq", "gt", "lt"]);
    if (!item.operator || !allowed.has(item.operator)) return null;
    const n = Number.parseFloat(String(item.filterValue ?? "").trim());
    if (!Number.isFinite(n) || n < 0) return null;
    return {
      filterType: item.filterType,
      operator: item.operator,
      value: n,
    };
  }
  if (item.filterType === "amountSpent") {
    const allowed = new Set(["gte", "lte", "eq", "gt", "lt"]);
    if (!item.operator || !allowed.has(item.operator)) return null;
    const dollars = Number.parseFloat(String(item.filterValue ?? "").trim());
    if (!Number.isFinite(dollars) || dollars < 0) return null;
    const cents = Math.round(dollars * 100);
    return {
      filterType: item.filterType,
      operator: item.operator,
      value: cents,
    };
  }
  if (item.filterType === "lastActive") {
    if (!item.operator) return null;
    if (FIELD_EXISTS_OPERATORS.has(item.operator)) {
      return {
        filterType: "lastActive",
        operator: item.operator,
      };
    }
    if (!LAST_ACTIVE_COMPARISON_OPERATORS.has(item.operator)) return null;
    const ymd = String(item.filterValue ?? "").trim();
    if (!YMD_DATE.test(ymd)) return null;
    return {
      filterType: "lastActive",
      operator: item.operator,
      value: ymd,
    };
  }
  return null;
}

export function apiFilterToFilterItem(
  api: Record<string, unknown>,
  id: string
): FilterItem {
  let filterType = (api.filterType as string) ?? "countryCode";
  if (filterType === "purchasedProduct") {
    filterType = "product";
  }
  const item: FilterItem = {
    id,
    filterType,
    countryCodes: filterType === "countryCode" ? ["US", "CA"] : undefined,
  };
  if (api.operator) item.operator = api.operator as string;
  if (filterType === "product" && !item.operator) item.operator = "has_purchased";
  if (
    TEXT_FIELD_FILTER_TYPES.has(filterType) &&
      (!item.operator ||
      (!TEXT_FIELD_OPERATORS.has(item.operator) &&
        !FIELD_EXISTS_OPERATORS.has(item.operator)))
  ) {
    const op = typeof api.operator === "string" ? api.operator : "";
    if (FIELD_EXISTS_OPERATORS.has(op)) {
      item.operator = op;
    } else {
      item.operator = TEXT_FIELD_OPERATORS.has(op) ? op : "contains";
    }
  }
  if (filterType === "lastActive") {
    if (
      !item.operator ||
      (!LAST_ACTIVE_COMPARISON_OPERATORS.has(item.operator) &&
        !FIELD_EXISTS_OPERATORS.has(item.operator))
    ) {
      item.operator = "gte";
    }
    if (
      LAST_ACTIVE_COMPARISON_OPERATORS.has(item.operator) &&
      api.value !== undefined &&
      api.value !== null
    ) {
      item.filterValue = String(api.value);
    }
  } else if (filterType === "numPurchases") {
    if (api.value !== undefined && api.value !== null) {
      item.filterValue = String(api.value);
    }
  } else if (filterType === "amountSpent") {
    if (api.value !== undefined && api.value !== null) {
      const cents = Math.round(Number(api.value));
      item.filterValue = Number.isFinite(cents)
        ? (cents / 100).toFixed(2)
        : undefined;
    }
  } else if (api.value !== undefined) {
    item.filterValue = String(api.value);
  }
  if (api.location && typeof api.location === "object") {
    const loc = api.location as { lat?: number; lng?: number };
    if (loc.lat != null) item.locationLat = loc.lat;
    if (loc.lng != null) item.locationLng = loc.lng;
  }
  if (api.pageId) item.pageId = String(api.pageId);
  if (api.formHtmlId && String(api.formHtmlId).trim()) {
    item.formHtmlId = String(api.formHtmlId).trim();
  }
  if (api.countryCodes && Array.isArray(api.countryCodes))
    item.countryCodes = api.countryCodes as string[];
  if (api.groupId) item.groupId = String(api.groupId);
  if (api.productId) item.productId = String(api.productId);
  if (api.variantId) item.variantId = String(api.variantId);
  return item;
}

export function createDefaultSmsRecipientFilters(): FilterItem[] {
  return [
    {
      id: crypto.randomUUID(),
      filterType: "countryCode",
      countryCodes: ["US", "CA"],
    },
  ];
}

export const DEFAULT_SMS_RECIPIENT_FILTERS = createDefaultSmsRecipientFilters();
